import * as vp from 'vopay';
import * as Yup from 'yup';
import { FieldData } from '@/components/Form/types';
import { useTranslation } from 'react-i18next';
import { ReportDetail } from '../types';

export type BasicOptions = {
  label: string;
  value: string;
  isVisible?: boolean;
};

const { Select, TextField, TextArea } = vp;

export function buildEditReportItemBasicFields(
  subscriptions?: vp.SubscriptionItem[],
  reportItemLabel?: string,
  reportItemValue?: string,
): FieldData<ReportDetail>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Reports.Form' });
  const dateFormatOptions = Object.entries(vp.SUBSCRIPTION_DATE_FORMAT_SELECT_OPTIONS).map(([key, value]) => ({
    label: key,
    value,
  }));
  const frequencyTypeOptions = [
    { label: t('SelectOptions.FrequencyOptions.daily'), value: 'daily' },
    { label: t('SelectOptions.FrequencyOptions.weekly'), value: 'weekly' },
    { label: t('SelectOptions.FrequencyOptions.monthly'), value: 'monthly' },
  ];
  return [
    {
      fieldName: 'ReportName',
      getInputState: () => 'optional',
      showField: () => reportItemLabel === 'Report Name',
      serializeField: (values) => values.ReportName?.replace(/, /g, ' ').replace(/,/g, ''),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder={t('Placeholder.reportName')} {...fieldData} />;
      },
      validate: () =>
        Yup.string()
          .test('unique-name-validation', t('ValidationMessage.ReportName.unique'), (value) => {
            const check = Object.values(subscriptions || {}).some(
              (data) => data.ReportName.toLowerCase() === value?.toLocaleLowerCase(),
            );
            return reportItemValue?.toLowerCase() === value?.toLocaleLowerCase() || !check;
          })
          .max(45, t('ValidationMessage.ReportName.characterLimit'))
          .matches(/^[A-Za-z0-9 ]+$/, t('ValidationMessage.ReportName.noSpecialCharacter'))
          .trim()
          .required(),
    },
    {
      fieldName: 'Description',
      getInputState: () => 'optional',
      showField: () => reportItemLabel === 'Description',
      serializeField: (values) => values.Description,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextArea
            height="90px"
            width="400px"
            placeholder={t('Placeholder.description')}
            maxLength={220}
            showcaseLength
            {...fieldData}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
    {
      fieldName: 'DateFormat',
      getInputState: () => 'optional',
      showField: () => reportItemLabel === 'Date Format',
      serializeField: (values) => values.DateFormat,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <Select version="2" placeholder={t('Placeholder.select')} options={dateFormatOptions} {...fieldData} />;
      },
      validate: () => Yup.string().required(),
    },
    {
      fieldName: 'Frequency',
      getInputState: () => 'optional',
      showField: () => reportItemLabel === 'Frequency',
      serializeField: (values) => values.Frequency,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select version="2" placeholder={t('Placeholder.select')} options={frequencyTypeOptions} {...fieldData} />
        );
      },
      validate: () => Yup.string().required(),
    },
  ];
}
